<template>
    <div class='settings-page'>
        <div class='settings-content'>
            <header class='page-header'>
                <div>
                    <p class='page-kicker'>BASIC SETTINGS</p>
                    <h1 class='title is-3'>デフォルト設定</h1>
                    <p class='page-description'>新しく作成する行事に適用される、決済・配送・写真サイズの初期値です。</p>
                </div>
            </header>

            <section class='setting-card'>
                <div class='section-heading'>
                    <div class='section-icon payment-icon'>
                        <i class='fas fa-credit-card' aria-hidden='true'></i>
                    </div>
                    <div>
                        <h2>決済方法</h2>
                        <p>保護者が利用できる決済方法と手数料を設定します。</p>
                    </div>
                </div>
                <div class='setting-table'>
                    <div class='setting-table-header'>
                        <span>決済方法</span>
                        <span>手数料</span>
                    </div>
                    <div v-for='(payment, i) in payments' :key='i' class='setting-row'>
                        <label class='item-toggle'>
                            <input type='checkbox' v-model='payments[i].use'>
                            <span>{{ payment.name }}</span>
                        </label>
                        <div class='price-control'>
                            <template v-if='payment.name === paymentMethod.CREDIT_CARD'>
                                <input type='text' class='input price-input' v-model='cardFee' disabled>
                                <span class='unit'>%</span>
                            </template>
                            <template v-else-if='payment.name === paymentMethod.CONVENIENCE_STORE'>
                                <input type='text' class='input price-input' v-model='conveniFee' disabled>
                                <span class='unit'>円</span>
                            </template>
                            <span v-else-if='isCustomerBornePaymentFee(payment)' class='fee-note'>{{ customerBornePaymentFeeText }}</span>
                            <template v-else>
                                <input type='number' class='input price-input' min='0' v-model='payments[i].price'>
                                <span class='unit'>円</span>
                            </template>
                        </div>
                    </div>
                </div>
            </section>

            <section class='setting-card'>
                <div class='section-heading'>
                    <div class='section-icon delivery-icon'>
                        <i class='fas fa-truck' aria-hidden='true'></i>
                    </div>
                    <div>
                        <h2>配送方法</h2>
                        <p>注文時に選択できる配送方法と送料を設定します。</p>
                    </div>
                </div>
                <div class='setting-table'>
                    <div class='setting-table-header'>
                        <span>配送方法</span>
                        <span>送料</span>
                    </div>
                    <div v-for='(delivery, i) in deliverys' :key='i' class='setting-row'>
                        <label class='item-toggle'>
                            <input type='checkbox' v-model='deliverys[i].use'>
                            <span>{{ delivery.name }}</span>
                        </label>
                        <div class='price-control'>
                            <input
                                type='number'
                                class='input price-input'
                                v-model='deliverys[i].price'
                                :min='delivery.name === deliveryMethod.YUU_PACKET ? yuuPacketFee : 0'
                            >
                            <span class='unit'>円</span>
                        </div>
                    </div>
                </div>
                <p v-if='Number(yuuPacketFee)' class='section-note'>
                    <i class='fas fa-info-circle' aria-hidden='true'></i>
                    ゆうパケット便は {{ yuuPacketFee }}円以上で設定できます。
                </p>
            </section>

            <section class='setting-card'>
                <div class='section-heading'>
                    <div class='section-icon size-icon'>
                        <i class='fas fa-image' aria-hidden='true'></i>
                    </div>
                    <div>
                        <h2>写真サイズと価格</h2>
                        <p>販売する写真サイズと、サイズごとの価格を設定します。</p>
                    </div>
                </div>
                <div class='setting-table'>
                    <div class='setting-table-header'>
                        <span>写真サイズ</span>
                        <span>販売価格</span>
                    </div>
                    <div v-for='(size, i) in sizes' :key='i' class='setting-row'>
                        <label class='item-toggle'>
                            <input type='checkbox' v-model='sizes[i].use'>
                            <span>{{ size.name }}</span>
                        </label>
                        <div class='price-control'>
                            <input type='number' class='input price-input' min='0' v-model='sizes[i].price'>
                            <span class='unit'>円</span>
                        </div>
                    </div>
                </div>
                <div class='default-setting'>
                    <div>
                        <label for='basic-default-size'>購入時のデフォルトサイズ</label>
                        <p>購入画面を開いたときに最初に選択されるサイズです。</p>
                    </div>
                    <div class='select'>
                        <select id='basic-default-size' v-model='defaultSize'>
                            <option v-for='(size, i) in sizes' :key='i' :value='size.name'>
                                {{ size.name }}
                            </option>
                        </select>
                    </div>
                </div>
            </section>

            <div class='save-bar'>
                <p>変更内容は保存後、新しく作成する行事へ反映されます。</p>
                <button class='button is-info is-medium' @click='save'>
                    <span class='icon'><i class='fas fa-save' aria-hidden='true'></i></span>
                    <span>デフォルト設定を保存</span>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { CUSTOMER_BORNE_PAYMENT_FEE_TEXT, isCustomerBornePaymentFee } from '../../../helpers/payment'
import { PAYMENT_METHOD, DELIVERY_METHOD } from '../../../constants/orderOption'

export default {
    name: 'Setting',
    components: {
    },
    data () {
        return {
            cardFee: 0,
            conveniFee: 0,
            yuuPacketFee: 0,
            paymentMethod: PAYMENT_METHOD,
            deliveryMethod: DELIVERY_METHOD,
            customerBornePaymentFeeText: CUSTOMER_BORNE_PAYMENT_FEE_TEXT,
            payments: [],
            sizes: [],
            deliverys: [],
            defaultSize: ''
        }
    },
    mounted () {
        const self = this
        const params = this.$parent.requestParams

        self.axios.get(`${process.env.API_URL}/photographer/basicSetting${params}`).then((res) => {
            const setting = res.data.setting

            self.payments = setting.payments
            self.sizes = setting.sizes
            self.deliverys = setting.deliverys
            self.defaultSize = setting.defaultSize

            self.cardFee = res.data.cardFee
            self.conveniFee = res.data.conveniFee
            self.yuuPacketFee = res.data.yuuPacketFee
        }).catch((e) => {
            console.log(e)
            alert('サーバーに問題が発生しています')
        })
    },
    methods: {
        isCustomerBornePaymentFee,
        save () {
            const self = this
            const params = this.$parent.requestParams
            const yuuPacketDelivery = self.deliverys.find((delivery) => {
                return delivery.name === DELIVERY_METHOD.YUU_PACKET
            })
            const minYuuPacketFee = Number(self.yuuPacketFee)

            if (yuuPacketDelivery && yuuPacketDelivery.use !== false && Number(yuuPacketDelivery.price) < minYuuPacketFee) {
                alert(`ゆうパケット便送料は${minYuuPacketFee}円未満には設定できません`)
                return
            }

            self.axios.post(`${process.env.API_URL}/photographer/basicSetting${params}`, {
                payments: self.payments,
                sizes: self.sizes,
                deliverys: self.deliverys,
                defaultSize: self.defaultSize
            }).then((res) => {
                if (res.data.message === 'OK') {
                    alert('保存が完了しました')
                } else if (res.data.message === 'YuuPacketFeeTooLow') {
                    alert(`ゆうパケット便送料は${res.data.yuuPacketFee}円未満には設定できません`)
                } else {
                    throw new Error()
                }
            }).catch((e) => {
                alert('サーバーに問題が発生しています')
            })
        }
    }
}
</script>

<style scoped>

.settings-page {
    width: 100%;
    padding: 34px 36px 64px 76px;
    background: #f6f8fb;
}

.settings-content {
    width: 100%;
    max-width: 960px;
}

.page-header {
    margin-bottom: 24px;
}

.page-header .title {
    margin: 2px 0 8px;
    color: #1f2937;
}

.page-kicker {
    color: #3273dc;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.14em;
}

.page-description {
    color: #667085;
    font-size: 14px;
}

.setting-card {
    overflow: hidden;
    margin-bottom: 20px;
    border: 1px solid #e4e9f1;
    border-radius: 10px;
    background: #fff;
    box-shadow: 0 2px 8px rgba(31, 41, 55, 0.04);
}

.section-heading {
    display: flex;
    gap: 14px;
    align-items: center;
    padding: 22px 24px 18px;
}

.section-heading h2 {
    margin: 0 0 3px;
    color: #25324b;
    font-size: 17px;
    font-weight: 700;
}

.section-heading p {
    margin: 0;
    color: #7a8496;
    font-size: 13px;
}

.section-icon {
    display: flex;
    flex: 0 0 38px;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border-radius: 9px;
}

.payment-icon {
    color: #3273dc;
    background: #edf4ff;
}

.delivery-icon {
    color: #16836b;
    background: #eaf8f4;
}

.size-icon {
    color: #9a6700;
    background: #fff6dd;
}

.setting-table {
    border-top: 1px solid #edf0f5;
}

.setting-table-header,
.setting-row {
    display: grid;
    grid-template-columns: minmax(220px, 1fr) minmax(180px, 260px);
    align-items: center;
    padding: 0 24px;
}

.setting-table-header {
    min-height: 38px;
    color: #8992a3;
    background: #fafbfc;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
}

.setting-row {
    min-height: 58px;
    border-top: 1px solid #f0f2f5;
}

.item-toggle {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    color: #344054;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
}

.item-toggle input {
    width: 17px;
    height: 17px;
    accent-color: #3273dc;
}

.price-control {
    display: flex;
    align-items: center;
    gap: 9px;
}

.price-input {
    width: 120px;
    height: 36px;
    text-align: right;
}

.price-input:disabled {
    color: #667085;
    background: #f2f4f7;
}

.unit {
    min-width: 18px;
    color: #667085;
    font-size: 13px;
}

.fee-note {
    display: inline-flex;
    align-items: center;
    min-height: 30px;
    padding: 4px 10px;
    border-radius: 999px;
    color: #475467;
    background: #f2f4f7;
    font-size: 12px;
}

.section-note {
    margin: 0;
    padding: 12px 24px;
    border-top: 1px solid #edf0f5;
    color: #667085;
    background: #fafbfc;
    font-size: 12px;
}

.section-note i {
    margin-right: 5px;
    color: #3273dc;
}

.default-setting {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    padding: 20px 24px;
    border-top: 1px solid #e5e9f0;
    background: #f8faff;
}

.default-setting label {
    color: #344054;
    font-size: 14px;
    font-weight: 700;
}

.default-setting p {
    margin: 3px 0 0;
    color: #7a8496;
    font-size: 12px;
}

.default-setting .select,
.default-setting select {
    min-width: 220px;
}

.save-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    padding: 18px 20px;
    border: 1px solid #dfe5ee;
    border-radius: 10px;
    background: #fff;
    box-shadow: 0 6px 20px rgba(31, 41, 55, 0.08);
}

.save-bar p {
    margin: 0;
    color: #667085;
    font-size: 12px;
}

@media screen and (max-width: 768px) {
    .settings-page {
        padding: 24px 16px 48px;
    }

    .setting-table-header {
        display: none;
    }

    .setting-row {
        grid-template-columns: 1fr;
        gap: 10px;
        padding: 14px 18px;
    }

    .section-heading {
        align-items: flex-start;
        padding: 18px;
    }

    .default-setting,
    .save-bar {
        align-items: stretch;
        flex-direction: column;
    }

    .default-setting .select,
    .default-setting select,
    .save-bar .button {
        width: 100%;
    }
}

</style>



// WEBPACK FOOTER //
// src/components/photographer/pages/BasicSetting.vue